import * as React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import * as Api from '@ViewModels';
import { DEFAULT_NOTIFICATION_RATINGS, SatisfactionSurveyFormData } from '../utils';

interface UseSatisfactionFormInitializationProps {
	surveyData?: Api.ISatisfactionSurvey;
	isClone: boolean;
	setValue: UseFormSetValue<SatisfactionSurveyFormData>;
}

export const useSatisfactionFormInitialization = ({
	surveyData,
	isClone,
	setValue,
}: UseSatisfactionFormInitializationProps) => {
	// Create a mapping from old field IDs to new field IDs for cloning
	const [fieldIdMap, setFieldIdMap] = React.useState<Map<string, string>>(new Map());

	React.useEffect(() => {
		if (surveyData) {
			// Initialize basic survey info
			setValue('name', isClone ? `${surveyData.name} Copy` : surveyData.name);
			setValue('companyDisplayName', surveyData.companyDisplayName);
			setValue('intro', surveyData.intro);
			setValue('startDate', surveyData.startDate);
			if (surveyData.expirationDate) {
				setValue('expirationDate', surveyData.expirationDate);
				setValue('indefinitely', false);
			}
			setValue('customFormIsDisabled', false);
			setValue('customForm.name', surveyData.customForm?.name);

			// Handle custom form fields and cloning
			if (surveyData.customForm?.fields) {
				if (isClone) {
					// Only do ID regeneration when cloning
					const newFieldIdMap = new Map<string, string>();
					const clonedFields = surveyData.customForm.fields.map(field => {
						const newFieldId = uuid();
						newFieldIdMap.set(field.id, newFieldId);
						return {
							...field,
							id: newFieldId,
							name: newFieldId, // Generate new name field for response key isolation
							isHidden: field.isHidden,
							isOptional: field.isOptional,
							label: field.label,
							fieldType: field.fieldType,
							options:
								field.options?.map(option => ({
									...option,
									id: uuid(), // Generate new option ID for clone
									fieldId: newFieldId, // Update option fieldId to reference the new field ID
								})) || [],
						};
					});

					setValue('customForm.fields', clonedFields);
					setFieldIdMap(newFieldIdMap);

					// Update customFormMetaData with new field IDs for clones
					setValue(
						'customFormMetaData',
						surveyData.customFormMetaData?.map(meta => ({
							fieldId: newFieldIdMap.get(meta.fieldId) || meta.fieldId,
							isConditional: meta.isConditional,
							ratings: meta.ratings,
						})) || []
					);

					// Update tagGenerationSettings with new field IDs for clones
					if (surveyData.tagGenerationSettings?.customFormFieldTagSettings) {
						const clonedTagSettings = surveyData.tagGenerationSettings.customFormFieldTagSettings.map(tagSetting => {
							const newFieldId = newFieldIdMap.get(tagSetting.fieldId) || tagSetting.fieldId;
							return {
								...tagSetting,
								fieldId: newFieldId,
							};
						});

						// Update the entire tagGenerationSettings object to ensure proper sync
						const currentTagSettings = surveyData.tagGenerationSettings || {
							tagOnCompletion: false,
							completionTag: '',
							customFormFieldTagSettings: [],
						};

						const updatedTagSettings = {
							...currentTagSettings,
							customFormFieldTagSettings: clonedTagSettings,
						};

						setValue('tagGenerationSettings', updatedTagSettings);
					}
				} else {
					// When not cloning, use original data as-is
					setValue('customForm.fields', surveyData.customForm.fields);
					setValue('customFormMetaData', surveyData.customFormMetaData || []);
				}
			} else {
				// Initialize empty customFormMetaData if no fields exist
				setValue('customFormMetaData', surveyData.customFormMetaData || []);
			}

			// CRITICAL: Preserve notification settings with ratings [1,2,3,4,5] default
			setValue('notificationSettings', {
				isEnabled: surveyData.notificationSettings?.isEnabled || false,
				ratings: surveyData.notificationSettings?.ratings || [...DEFAULT_NOTIFICATION_RATINGS],
				additionalRecipients: surveyData.notificationSettings?.additionalRecipients || [],
			});

			// CRITICAL: Preserve review request settings
			setValue('reviewRequestSettings', {
				isEnabled: surveyData.reviewRequestSettings?.isEnabled || false,
				reviewUrl: surveyData.reviewRequestSettings?.reviewUrl || '',
				reviewType: surveyData.reviewRequestSettings?.reviewType || '',
				reviewRequest: surveyData.reviewRequestSettings?.reviewRequest || '',
				ratings: surveyData.reviewRequestSettings?.ratings || [],
			});

			setValue('shouldHideFeedback', surveyData.shouldHideFeedback ?? false);

			// Initialize tag generation settings for non-clone scenarios (editing existing surveys)
			// Note: Clone scenarios are handled in the clone block above with field ID mapping
			if (!isClone) {
				setValue(
					'tagGenerationSettings',
					surveyData.tagGenerationSettings || {
						tagOnCompletion: false,
						completionTag: '',
						customFormFieldTagSettings: [],
					}
				);
			}
		}
	}, [surveyData, isClone, setValue]);

	return { fieldIdMap };
};
